import { of, Observable, ReplaySubject, Subject, iif, throwError, EMPTY, timer } from 'rxjs';
import { switchMap, filter, take, catchError, map, mergeMap, shareReplay, tap, expand } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { dateToIsoString, removeWhitespaces } from '@nano/common/util';
import { ApiEndpoints, ApiErrorCode, ApiResponse, ApiService } from '@nano/core/api';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import { addOauthTfaHeaders, getOauthTfaMetadata, TfaFallbackMethod } from '@nano/core/oauth';
import { TfaTokenDetails, TfaType } from '@nano/core/tfa';
import {
  assignIsPaymentHasAdditionalInfo,
  BeneficiaryAddressStatus,
  BeneficiaryAddressSuggestion,
  normalizePaymentCustomFields,
  ValidateBeneficiaryAddressRequest
} from '@nano/payment-additional-info';
import {
  Payment,
  PaymentConditions,
  PaymentConfig,
  PaymentResponse,
  PaymentStatus,
  PaymentSummary,
  isPaymentBoa,
  isPaymentGateway,
  PayeeVerificationResponse,
  PayeeVerificationStatus
} from './payment';
import { TranslatableErrorCode } from '@nano/common/form';
import { SseService } from '@nano/core/sse';
import { HttpErrorResponse } from '@angular/common/http';
import { PaymentEntry } from '@nano/payment-entry';

@Injectable({ providedIn: 'root' })
export class PaymentService {
  paymentStatus$: Observable<PaymentStatus>;
  paymentSummary$: Observable<PaymentSummary>;

  suggestedBeneficiaryAddress: BeneficiaryAddressSuggestion;

  private paymentStatusSubject: Subject<PaymentStatus> = new Subject<PaymentStatus>();
  private paymentSummarySubject: Subject<PaymentSummary> = new ReplaySubject<PaymentSummary>(null);
  private _config: PaymentConfig;
  private oauthEnabled: boolean;
  private conditionsExchangeRate: number;
  private paymentConditionsCache = new Map<string, { expiresAt: number; request$: Observable<PaymentConditions> }>();
  private readonly PAYMENT_CONDITIONS_CACHE_TTL_MS = 1000;
  private readonly PAYEE_VERIFICATION_POLL_INTERVAL_MS = 1000;

  private appConfig = inject(CONFIG);

  constructor(
    private apiService: ApiService,
    private errorService: ErrorService,
    private sseService: SseService
  ) {
    this.paymentStatus$ = this.paymentStatusSubject.asObservable();
    this.paymentSummary$ = this.paymentSummarySubject.asObservable();
    this.readConfig();
  }

  get config(): PaymentConfig {
    return this._config;
  }

  get getConditionsExchangeRate(): number {
    return this.conditionsExchangeRate;
  }

  getPaymentConditions(payment: Payment): Observable<PaymentConditions> {
    const payerAccount = payment.payerAccount;
    const feeAccount = payment?.feeAccount;
    const requestPayment = { ...payment } as Payment;

    this.transformPayment(requestPayment);
    delete requestPayment.operationType;
    delete requestPayment.payerAccount;
    delete requestPayment.paymentDetailsType;
    delete requestPayment?.feeAccount;

    if (requestPayment.amount && requestPayment.initialAmount) {
      delete requestPayment.initialAmount;
    }

    const paymentConditionsParams = {
      payerAccountNumber: requestPayment.payerAccountNumber,
      currency: requestPayment.currency,
      beneficiaryAccountNumber: requestPayment.beneficiaryAccountNumber,
      amount: requestPayment.amount,
      beneficiaryBankIdentifier: requestPayment.beneficiaryBankIdentifier,
      periodType: requestPayment.periodType,
      fromDate: requestPayment.fromDate,
      tillDate: requestPayment.tillDate,
      initialAmount: requestPayment.initialAmount,
      initialCurrency: requestPayment.initialCurrency
        ? requestPayment.initialCurrency
        : requestPayment.otherCurrency !== requestPayment.currency
          ? requestPayment.otherCurrency
          : undefined,
      feeAccountNumber: requestPayment.feeAccountNumber,
      feeAccountCurrency: requestPayment.feeAccountCurrency,
      beneficiaryCategory: requestPayment.beneficiaryCategory
    } as Payment;

    if (requestPayment.beneficiaryCountry) {
      paymentConditionsParams.beneficiaryCountry = requestPayment.beneficiaryCountry;
    }

    if (requestPayment.beneficiaryBankCountry) {
      paymentConditionsParams.beneficiaryBankCountry = requestPayment.beneficiaryBankCountry;
    }

    if (requestPayment.targetPaymentSystem) {
      paymentConditionsParams.targetPaymentSystem = requestPayment.targetPaymentSystem;
    }

    if (requestPayment.payerIdentificationCodeType) {
      paymentConditionsParams.payerIdentificationCodeType = requestPayment.payerIdentificationCodeType;
    }

    const endpoint = requestPayment.scheduled
      ? ApiEndpoints.standingOrderConditions(paymentConditionsParams)
      : ApiEndpoints.paymentConditions(paymentConditionsParams);
    const cachedConditions = this.paymentConditionsCache.get(endpoint.url);

    if (cachedConditions) {
      if (cachedConditions.expiresAt > Date.now()) {
        return cachedConditions.request$;
      }

      this.paymentConditionsCache.delete(endpoint.url);
    }

    this.errorService.clearError();
    const request$ = this.apiService.get<PaymentConditions>(endpoint).pipe(
      map((conditions) => {
        this.conditionsExchangeRate = conditions.exchangeRate;
        if (conditions.initialAmount) {
          const feeAmount = conditions?.fee?.amount || 0;
          const totalAmount: number = parseFloat((conditions.initialAmount + feeAmount).toFixed(2));
          const totalCurrency = conditions.initialCurrency || conditions.currency;
          const sameAccount = !feeAccount || (payerAccount?.iban === feeAccount?.iban && payerAccount?.currency === feeAccount?.currency);
          const checkInsufficientFunds = (): boolean => {
            if (!requestPayment.waitForFunds) {
              if (conditions.feeDebtAllowed) {
                return payerAccount.availableFunds < conditions.initialAmount;
              } else if (sameAccount) {
                return payerAccount.availableFunds < totalAmount;
              } else {
                return payerAccount.availableFunds < conditions.initialAmount;
              }
            }
          };

          const amountError = checkInsufficientFunds() ? { code: TranslatableErrorCode.insufficientFunds } : null;

          const feeAccountError =
            !requestPayment.waitForFunds && !!feeAccount && feeAccount.availableFunds < feeAmount && !conditions.feeDebtAllowed
              ? { code: TranslatableErrorCode.insufficientFundsFeeAccount }
              : null;
          return {
            ...conditions,
            ...{
              total: { amount: totalAmount, currency: totalCurrency },
              amountError,
              feeAccountError
            }
          };
        } else {
          return { ...conditions };
        }
      }),
      catchError((error: unknown) => {
        this.paymentConditionsCache.delete(endpoint.url);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.paymentConditionsCache.set(endpoint.url, {
      expiresAt: Date.now() + this.PAYMENT_CONDITIONS_CACHE_TTL_MS,
      request$
    });

    return request$;
  }

  createPaymentOperation(payment: Payment, paymentConditions?: PaymentConditions): Observable<PaymentSummary> {
    this.transformPayment(payment);
    this.errorService.clearError();
    return this.initPayment$(payment, paymentConditions).pipe(mergeMap((paymentSummary) => this.initPaymentConfirmation$(paymentSummary)));
  }

  markPaymentConfirmed(): void {
    this.paymentStatusSubject.next(PaymentStatus.COMPLETED);
  }

  backToInitialStep(): void {
    this.paymentStatusSubject.next(null);
  }

  cleanUpPaymentSummary(): void {
    this.paymentSummarySubject.next(null);
  }

  savePaymentForLaterConfirmation(payment: Payment, paymentConditions?: PaymentConditions): Observable<PaymentSummary> {
    this.transformPayment(payment);
    this.errorService.clearError();
    payment.saveForLater = true;

    return this.initPayment$(payment, paymentConditions).pipe(tap(() => this.paymentStatusSubject.next(PaymentStatus.INITIATED)));
  }

  operationConfirmationOauthCheck$ = (paymentSummary: PaymentSummary, requestedSMS?: boolean): Observable<PaymentSummary> => {
    return this.apiService
      .put<PaymentSummary>(
        ApiEndpoints.operationConfirm(paymentSummary.operationId, paymentSummary.confirmationType),
        {
          confirmationData: paymentSummary.confirmationData
        },
        null,
        requestedSMS ? this.addOauthTfaHeaders(null, paymentSummary.txId, TfaFallbackMethod.SMS) : null
      )
      .pipe(
        mergeMap(() => this.paymentCreated$(paymentSummary)),
        catchError((error: unknown) => {
          const tfaMetadata = this.getOauthTfaMetadata(error);
          return !tfaMetadata?.txId ? throwError(error) : this.paymentCreated$(Object.assign(paymentSummary, tfaMetadata));
        })
      );
  };

  private addOauthTfaHeaders(tfa: string | null, txId?: string, additionalHeaders?: TfaFallbackMethod): ReturnType<typeof addOauthTfaHeaders> {
    return addOauthTfaHeaders(tfa, txId, additionalHeaders);
  }

  private getOauthTfaMetadata(error: unknown): ReturnType<typeof getOauthTfaMetadata> {
    return getOauthTfaMetadata(error);
  }

  deletePaymentOperation$(operationId: string): Observable<ApiResponse<unknown>> {
    return this.apiService.delete<ApiResponse>(ApiEndpoints.operation(operationId));
  }

  updatePaymentSummaryOnExchangeRateChange(payment: Payment): void {
    const payload = {
      beneficiaryAccountNumber: payment.beneficiaryAccountNumber,
      beneficiaryBankIdentifier: payment.beneficiaryBankIdentifier,
      payerAccount: payment.payerAccount,
      payerAccountNumber: payment.payerAccountNumber,
      beneficiaryCategory: payment.beneficiaryCategory,
      initialAmount: payment.initialAmount,
      initialCurrency: payment.initialCurrency,
      amount: payment.amount,
      currency: payment.currency
    } as Payment;
    this.getPaymentConditions(payload).subscribe((conditions) => {
      if (conditions.exchangeRate !== payment.exchangeRate) {
        const updatedSummary: PaymentSummary = {
          ...payment,
          exchangeRate: conditions.exchangeRate,
          amount: conditions.amount,
          initialAmount: conditions.initialAmount,
          fee: conditions.fee,
          total: conditions.total
        } as PaymentSummary;
        this.paymentSummarySubject.next(updatedSummary);
      }
    });
  }

  validateSepaBeneficiaryName(payload: { iban: string; name: string; payeeAgentBankCode: string }): Observable<PayeeVerificationResponse> {
    return this.startSepaBeneficiaryValidation(payload).pipe(
      switchMap((response: PayeeVerificationResponse) => {
        if (response.status !== PayeeVerificationStatus.RECEIVED) {
          return of(response);
        }
        return this.getSepaBeneficiaryValidationStatus(response.id).pipe(
          expand((res: PayeeVerificationResponse) =>
            [PayeeVerificationStatus.COMPLETED, PayeeVerificationStatus.FAILED, PayeeVerificationStatus.NOT_APPLICABLE].includes(res.status)
              ? EMPTY
              : timer(this.PAYEE_VERIFICATION_POLL_INTERVAL_MS).pipe(switchMap(() => this.getSepaBeneficiaryValidationStatus(response.id)))
          ),
          filter((res: PayeeVerificationResponse) =>
            [PayeeVerificationStatus.COMPLETED, PayeeVerificationStatus.FAILED, PayeeVerificationStatus.NOT_APPLICABLE].includes(res.status)
          ),
          take(1),
          catchError(() => of(this.createFailedPayeeVerificationResponse(payload, response.id)))
        );
      }),
      catchError(() => of(this.createFailedPayeeVerificationResponse(payload)))
    );
  }

  private createFailedPayeeVerificationResponse(payload: { iban: string; name: string; payeeAgentBankCode: string }, id = ''): PayeeVerificationResponse {
    return {
      createdAt: new Date().toISOString(),
      id,
      request: {
        iban: payload.iban,
        name: payload.name,
        partyAgentBankCode: payload.payeeAgentBankCode
      },
      status: PayeeVerificationStatus.FAILED
    };
  }

  startSepaBeneficiaryValidation(payload: { iban: string; name: string; payeeAgentBankCode: string }): Observable<PayeeVerificationResponse> {
    return this.apiService.post<PayeeVerificationResponse>(ApiEndpoints.payeeVerifications(), this.createPayeeVerificationRequestBody(payload));
  }

  getSepaBeneficiaryValidationStatus(id: string): Observable<PayeeVerificationResponse> {
    return this.apiService.get<PayeeVerificationResponse>(ApiEndpoints.payeeVerificationById(id));
  }

  validateBeneficiaryAdress(payment: Payment, conditions: PaymentConditions, saveforLater?: boolean): Observable<BeneficiaryAddressSuggestion> {
    const payload: ValidateBeneficiaryAddressRequest = {
      address: {
        addressLine: payment.beneficiaryAddress || '',
        buildingName: payment.beneficiaryBuildingName || '',
        buildingNumber: payment.beneficiaryBuildingNumber || '',
        city: payment.beneficiaryCity || '',
        country: payment.beneficiaryCountry || '',
        countrySubDivision: payment.beneficiaryProvince || '',
        postCode: payment.beneficiaryPostalCode || '',
        street: payment.beneficiaryStreetName || ''
      },
      paymentSystem: conditions.restfulPayPaymentSystem
    };

    return this.initBeneficiaryAddressValidation$(payload).pipe(
      switchMap((response) => {
        if (response.status === BeneficiaryAddressStatus.INVALID) {
          this.suggestedBeneficiaryAddress = null;
          return of(response);
        }

        if (!payment.beneficiaryAddress && response.address) {
          const { buildingNumber, street, city, country } = response.address;
          if (buildingNumber || street || city || country) {
            response.address.addressLine = null;
          }
        }
        this.suggestedBeneficiaryAddress = response;
        assignIsPaymentHasAdditionalInfo(payment);
        payment.saveForLater = saveforLater;
        payment.exchangeRate = conditions.exchangeRate;
        payment.exchangeRateCurrencyPair = conditions.exchangeRateCurrencyPair;
        payment.targetPaymentSystem = conditions.targetPaymentSystem;
        this.paymentSummarySubject.next(payment as PaymentSummary);

        const hasDifferences = this.hasDifferentSuggestions(payment, response);

        if (hasDifferences) {
          this.paymentStatusSubject.next(PaymentStatus.VALIDATING_ADDRESS);
          return of(response);
        } else if (saveforLater) {
          return this.savePaymentForLaterConfirmation(payment, conditions).pipe(map(() => response));
        } else {
          return this.createPaymentOperation(payment).pipe(map(() => response));
        }
      }),
      map(() => this.suggestedBeneficiaryAddress)
    );
  }

  private initPayment$ = (payment: Payment, paymentConditions?: PaymentConditions): Observable<PaymentSummary> => {
    const requestBody = { ...payment };
    if (isPaymentBoa(requestBody.operationType) && !requestBody.paymentDetails && !requestBody.paymentCode) {
      delete requestBody.paymentDetailsType;
    }
    if (requestBody.exchangeRate) {
      delete requestBody.exchangeRate;
    }
    delete requestBody.payerAccount;
    delete requestBody.attachments;

    if (isPaymentGateway(requestBody.operationType)) {
      if (paymentConditions?.targetPaymentSystem) {
        requestBody.targetPaymentSystem = paymentConditions.targetPaymentSystem;
      }
    } else {
      delete requestBody.targetPaymentSystem;
    }

    const conditionsCustomFields = paymentConditions?.customFields;
    const conditionsToMerge: Partial<PaymentConditions> = { ...paymentConditions };
    delete conditionsToMerge.customFields;
    const formCustomFields = payment.customFields as unknown;

    return this.apiService.post<PaymentResponse>(payment.scheduled ? ApiEndpoints.standingOrders() : ApiEndpoints.payments(), requestBody).pipe(
      map((paymentResponse) => {
        this.processCustomFields(paymentResponse.result.confirmationData);
        return paymentResponse.result;
      }),
      map((paymentResult): PaymentSummary => {
        const paymentSummary: PaymentSummary = Object.assign(
          payment,
          {
            operationId: paymentResult.operationId,
            beneficiaryBankCode: paymentResult.confirmationData.beneficiaryBank,
            correspondentBankAddress: paymentResult.confirmationData.intermediaryInstitutionAddress,
            correspondentBankCode: paymentResult.confirmationData.intermediaryInstitutionCode,
            correspondentBankIdentifier: paymentResult.confirmationData.intermediaryInstitutionIdentifier,
            correspondentBankName: paymentResult.confirmationData.intermediaryInstitutionName,
            correspondentBankCountry: paymentResult.confirmationData.intermediaryInstitutionCountry
          },
          paymentResult.confirmationData,
          conditionsToMerge
        );

        if (!paymentSummary.customFields?.length) {
          paymentSummary.customFields = normalizePaymentCustomFields(formCustomFields, conditionsCustomFields);
        }

        return paymentSummary;
      }),
      catchError((httpError: HttpErrorResponse) => {
        const error = httpError.error as ApiResponse;
        const errorCode = error.metadata?.messages?.[0]?.code;
        if ((errorCode as ApiErrorCode) === ApiErrorCode.currencyMismatch) {
          this.sseService.exchangeRateChangedSubject.next({ type: 'EXCHANGE_RATE_UPDATED', data: null } as MessageEvent);
        }
        this.errorService.handleHttpError(httpError);
        return EMPTY;
      })
    );
  };

  private initPaymentConfirmation$ = (paymentSummary: PaymentSummary): Observable<PaymentSummary> =>
    this.apiService.post<TfaTokenDetails>(ApiEndpoints.operationConfirm(paymentSummary.operationId), {}).pipe(
      map((tokenDetails) => {
        if ((isPaymentBoa(paymentSummary.operationType) && !paymentSummary.scheduled) || tokenDetails.confirmationType === 'NO_TFA') {
          tokenDetails.tfaType = TfaType.NONE;
        }
        return Object.assign(paymentSummary, tokenDetails);
      }),
      mergeMap((paymentSummary) =>
        iif(
          () => this.oauthEnabled && paymentSummary.tfaType !== TfaType.NONE,
          this.operationConfirmationOauthCheck$(paymentSummary),
          this.paymentCreated$(paymentSummary)
        )
      )
    );

  private paymentCreated$ = (paymentSummary: PaymentSummary): Observable<PaymentSummary> =>
    of(paymentSummary).pipe(
      tap(() => {
        this.paymentStatusSubject.next(PaymentStatus.CREATED);
        assignIsPaymentHasAdditionalInfo(paymentSummary);
        this.paymentSummarySubject.next(paymentSummary);
      })
    );

  private transformPayment(payment: Payment): void {
    payment.beneficiaryAccountNumber = removeWhitespaces(payment.beneficiaryAccountNumber)?.toUpperCase();
    payment.beneficiaryBankIdentifier = removeWhitespaces(payment.beneficiaryBankIdentifier)?.toUpperCase();

    if (payment.fromDate) {
      payment.fromDate = dateToIsoString(payment.fromDate);
    }

    if (payment.tillDate) {
      payment.tillDate = dateToIsoString(payment.tillDate);
    }

    if (payment.payerBirthDate) {
      payment.payerBirthDate = dateToIsoString(payment.payerBirthDate);
    }

    if (payment.initialPayerBirthDate) {
      payment.initialPayerBirthDate = dateToIsoString(payment.initialPayerBirthDate);
    }

    if (payment.beneficiaryBirthDate) {
      payment.beneficiaryBirthDate = dateToIsoString(payment.beneficiaryBirthDate);
    }

    if (payment.ultimateBeneficiaryBirthDate) {
      payment.ultimateBeneficiaryBirthDate = dateToIsoString(payment.ultimateBeneficiaryBirthDate);
    }

    payment.attachmentIds = payment.attachments?.map((attachment) => attachment.id as string);
  }

  private readConfig(): void {
    this.oauthEnabled = this.appConfig.oauthEnabled;
    this._config = {
      beneficiaryAccountNumberMinLength: this.appConfig.paymentBeneficiaryAccountNumberMinLength,
      beneficiaryAccountNumberMaxLength: this.appConfig.paymentBeneficiaryAccountNumberMaxLength,
      beneficiaryNameMinLength: this.appConfig.paymentBeneficiaryNameMinLength,
      beneficiaryNameMaxLength: this.appConfig.paymentBeneficiaryNameMaxLength,
      beneficiaryBankNameMaxLength: this.appConfig.paymentBeneficiaryBankNameMaxLength,
      beneficiaryBankIdentifierMaxLength: this.appConfig.paymentBeneficiaryBankIdentifierMaxLength,
      beneficiaryBankAddressMaxLength: this.appConfig.paymentBeneficiaryBankAddressMaxLength,
      detailsMaxLength: this.appConfig.paymentDetailsMaxLength,
      paymentCodeMaxLength: this.appConfig.paymentCodeMaxLength,
      amountMin: this.appConfig.paymentAmountMin,
      amountMax: this.appConfig.paymentAmountMax,
      amountPrecision: this.appConfig.paymentAmountPrecision,
      beneficiaryAddressMaxLength: this.appConfig.paymentBeneficiaryAddressMaxLength,
      beneficiaryPostalCodeMaxLength: this.appConfig.addressPostalCodeMaxLength,
      correspondentBankCodeMaxLength: this.appConfig.paymentCorrespondentBankCodeMaxLength,
      correspondentBankNameMaxLength: this.appConfig.paymentCorrespondentBankNameMaxLength,
      correspondentBankAddressMaxLength: this.appConfig.paymentCorrespondentBankAddressMaxLength,
      correspondentBankIdentifierMaxLength: this.appConfig.paymentCorrespondentBankIdentifierMaxLength,
      identificationMaxLength: this.appConfig.paymentIdentificationMaxLength,
      identificationNameMaxLength: this.appConfig.paymentIdentificationNameMaxLength,
      identificationProvinceOfBirthMaxLength: this.appConfig.paymentIdentificationProvinceOfBirthMaxLength,
      identificationCityOfBirthMaxLength: this.appConfig.paymentIdentificationCityOfBirthMaxLength,
      identificationAddressMaxLength: this.appConfig.paymentIdentificationAddressMaxLength,
      scheduledPaymentsEnabled: this.appConfig.scheduledPaymentsEnabled,
      feeAccountEnabled: this.appConfig.paymentFeeAccountEnabled,
      addressCountryMaxLength: this.appConfig.addressCountryMaxLength,
      paymentIdentificationMaxLength: this.appConfig.paymentIdentificationMaxLength,
      beneficiaryAccountPattern: this.appConfig.beneficiaryAccountPattern
    };
  }

  private initBeneficiaryAddressValidation$(payload: ValidateBeneficiaryAddressRequest): Observable<BeneficiaryAddressSuggestion> {
    return this.apiService.post<BeneficiaryAddressSuggestion>(ApiEndpoints.paymentsBeneficiariesValidation(), payload);
  }

  private createPayeeVerificationRequestBody(payload: { iban: string; name: string; payeeAgentBankCode: string }): {
    iban: string;
    name: string;
    partyAgentBankCode: string;
  } {
    return {
      iban: removeWhitespaces(payload.iban)?.toUpperCase(),
      name: payload.name,
      partyAgentBankCode: removeWhitespaces(payload.payeeAgentBankCode)?.toUpperCase()
    };
  }

  private hasDifferentSuggestions(payment: Payment, suggestion: BeneficiaryAddressSuggestion): boolean {
    if (!suggestion?.address) {
      return false;
    }

    const address = suggestion.address;

    return (
      this.shouldShowSuggestion(payment.beneficiaryCountry, address.country) ||
      this.shouldShowSuggestion(payment.beneficiaryProvince, address.countrySubDivision) ||
      this.shouldShowSuggestion(payment.beneficiaryCity, address.city) ||
      this.shouldShowSuggestion(payment.beneficiaryStreetName, address.street) ||
      this.shouldShowSuggestion(payment.beneficiaryBuildingNumber, address.buildingNumber) ||
      this.shouldShowSuggestion(payment.beneficiaryBuildingName, address.buildingName) ||
      this.shouldShowSuggestion(payment.beneficiaryAddress, address.addressLine) ||
      this.shouldShowSuggestion(payment.beneficiaryPostalCode, address.postCode)
    );
  }

  private shouldShowSuggestion(userValue: string, suggestedValue: string): boolean {
    return !!suggestedValue && userValue !== suggestedValue;
  }

  private processCustomFields(confirmationData: PaymentEntry): void {
    if (!confirmationData) {
      return;
    }

    confirmationData.customFields = normalizePaymentCustomFields(confirmationData.customFields, confirmationData.customFieldsMetadata);
  }
}
